// Attendance settings drawer — how the mobile app tracks position and what a
// punch needs (general-settings, numeric wire). The drawer edits a local
// DRAFT seeded from the saved value on every open; "Save changes" is enabled
// only once the draft differs AND validates, and hands the draft to the
// caller, who runs the create-or-update and closes on success. Cancel, the X
// and the overlay discard. The drawer is also the ONLY entry point to the
// company's API keys: "Manage API keys" opens ApiKeysModal stacked above it.

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ConfirmModal,
  Drawer,
  Field,
  Input,
  SegmentedControl,
  SelectDropdown,
  Switch,
} from '@/components/common';
import {
  RETENTION_MAX_DAYS,
  RETENTION_MIN_DAYS,
  TRACKING_DISTANCES,
  TRACKING_INTERVALS,
  type AttendanceTrackingInterval,
  type SaveAttendanceSettingsInput,
} from '@/api/endpoints/generalSettingsApi';
import ApiKeysModal from './api-keys/ApiKeysModal';

/** The drawer's model IS the save body (wire names, numbers). */
export type AttendanceSettings = SaveAttendanceSettingsInput;

/** The two-way "Mandatory / Optional" groups, as segmented values. */
const REQUIRED = 'required';
const OPTIONAL = 'optional';
const toRequirement = (required: boolean) => (required ? REQUIRED : OPTIONAL);

/** A whole number of days inside the backend's accepted range. */
const parseRetention = (text: string): number | null => {
  if (!/^\d+$/.test(text.trim())) return null;
  const days = Number(text);
  return days >= RETENTION_MIN_DAYS && days <= RETENTION_MAX_DAYS ? days : null;
};

interface AttendanceSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The persisted settings the draft reseeds from on every open. */
  value: AttendanceSettings;
  /** Save changes — the caller persists (create or update) and closes on success. */
  onSave: (next: AttendanceSettings) => void;
  /** The settings fetch has succeeded — saving before it resolves could
   *  mis-branch into create, so Save stays disabled until then. */
  ready: boolean;
  saving: boolean;
  /** Fetch or save failed — the drawer stays open and says so. */
  error: boolean;
}

const AttendanceSettingsDrawer = ({
  open,
  onClose,
  value,
  onSave,
  ready,
  saving,
  error,
}: AttendanceSettingsDrawerProps) => {
  const { t } = useTranslation();
  const distanceId = useId();
  const retentionId = useId();

  const [draft, setDraft] = useState<AttendanceSettings>(value);
  // Retention is typed freely (a half-typed "1" on the way to "15" must not
  // snap), so its text lives beside the numeric draft until it validates.
  const [retentionText, setRetentionText] = useState(String(value.retentionPeriod));
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  // Every-minute capture drains batteries — picking it raises a confirm and
  // the value applies only once accepted.
  const [minuteConfirmOpen, setMinuteConfirmOpen] = useState(false);

  // Reseed on open so a discarded draft never leaks into the next session.
  useEffect(() => {
    if (open) {
      setDraft(value);
      setRetentionText(String(value.retentionPeriod));
      setApiKeysOpen(false);
      setMinuteConfirmOpen(false);
    }
  }, [open, value]);

  // The Drawer closes on a document-level Escape; while a modal (API keys,
  // the every-minute confirm) is stacked above, that keystroke belongs to the
  // modal, so the drawer's close is held off (a ref — the Drawer binds its
  // handler once per open).
  const stackedOpenRef = useRef(false);
  stackedOpenRef.current = apiKeysOpen || minuteConfirmOpen;
  const close = () => {
    if (!stackedOpenRef.current) onClose();
  };

  const patch = (changes: Partial<AttendanceSettings>) => setDraft((d) => ({ ...d, ...changes }));

  const retention = parseRetention(retentionText);
  const retentionInvalid = retention === null;
  const next: AttendanceSettings = {
    ...draft,
    retentionPeriod: retention ?? value.retentionPeriod,
  };
  const dirty =
    next.isTrackingEnabled !== value.isTrackingEnabled ||
    next.trackLocationPeriod !== value.trackLocationPeriod ||
    next.trackLocationDistance !== value.trackLocationDistance ||
    next.retentionPeriod !== value.retentionPeriod ||
    next.checkInRequired !== value.checkInRequired ||
    next.insideGeoFenceRequired !== value.insideGeoFenceRequired ||
    next.attendanceMediaRequired !== value.attendanceMediaRequired;
  const canSave = ready && dirty && !retentionInvalid;

  const bounds = { min: RETENTION_MIN_DAYS, max: RETENTION_MAX_DAYS };
  const requirementOptions = [
    { value: REQUIRED, label: t('attendance_settings_mandatory') },
    { value: OPTIONAL, label: t('attendance_settings_optional') },
  ];

  return (
    <Drawer
      open={open}
      onClose={close}
      closeOnOverlay
      className='attendance-settings'
      icon='settings'
      title={t('attendance_settings_title')}
      subtitle={t('attendance_settings_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button
            iconStart='check'
            onClick={() => onSave(next)}
            loading={saving}
            disabled={!canSave}
          >
            {t('attendance_settings_save')}
          </Button>
        </>
      }
    >
      <div className='attendance-settings__body'>
        {error && <Alert variant='danger'>{t('error_generic')}</Alert>}

        <div className='attendance-settings__row'>
          <div className='attendance-settings__row-text'>
            <span className='attendance-settings__row-label'>
              {t('attendance_settings_tracking')}
            </span>
            <span className='attendance-settings__hint'>
              {t('attendance_settings_tracking_hint')}
            </span>
          </div>
          <Switch
            checked={draft.isTrackingEnabled}
            onChange={(e) => patch({ isTrackingEnabled: e.target.checked })}
            label={t(
              draft.isTrackingEnabled ? 'attendance_settings_on' : 'attendance_settings_off',
            )}
            size='lg'
          />
        </div>

        {/* Interval and distance only matter while tracking is on — both
            disable with the switch, mirroring the hint's "off = punches only".
            Retention still governs the points already stored, so it stays. */}
        <div className='attendance-settings__control'>
          <span className='attendance-settings__control-label'>
            {t('attendance_settings_interval')}
          </span>
          <SegmentedControl
            aria-label={t('attendance_settings_interval')}
            options={TRACKING_INTERVALS.map((mins) => ({
              value: String(mins),
              label: t(`attendance_interval_${mins}`),
            }))}
            value={String(draft.trackLocationPeriod)}
            onChange={(v) => {
              const mins = Number(v) as AttendanceTrackingInterval;
              if (mins === 1) setMinuteConfirmOpen(true);
              else patch({ trackLocationPeriod: mins });
            }}
            disabled={!draft.isTrackingEnabled}
          />
          <span className='attendance-settings__hint'>
            {t('attendance_settings_interval_hint')}
          </span>
        </div>

        <div className='attendance-settings__grid'>
          <Field
            label={t('attendance_settings_distance')}
            hint={t('attendance_settings_distance_hint')}
            htmlFor={distanceId}
          >
            <SelectDropdown
              id={distanceId}
              options={TRACKING_DISTANCES.map((m) => ({
                value: String(m),
                label: t('attendance_distance_m', { m }),
              }))}
              value={String(draft.trackLocationDistance)}
              onChange={(v) => patch({ trackLocationDistance: Number(v) })}
              iconStart='move'
              disabled={!draft.isTrackingEnabled}
              maxHeight={280}
            />
          </Field>
          <Field
            label={t('attendance_settings_retention')}
            hint={t('attendance_settings_retention_hint', bounds)}
            error={
              retentionInvalid ? t('attendance_settings_retention_invalid', bounds) : undefined
            }
            htmlFor={retentionId}
          >
            <span className='attendance-settings__unit-input'>
              <Input
                id={retentionId}
                type='number'
                inputMode='numeric'
                size='sm'
                min={RETENTION_MIN_DAYS}
                max={RETENTION_MAX_DAYS}
                step={1}
                value={retentionText}
                error={retentionInvalid}
                onChange={(e) => setRetentionText(e.target.value)}
              />
              <span className='attendance-settings__unit' aria-hidden='true'>
                {t('attendance_settings_days')}
              </span>
            </span>
          </Field>
        </div>

        <hr className='attendance-settings__divider' />

        <div className='attendance-settings__row'>
          <div className='attendance-settings__row-text'>
            <span className='attendance-settings__row-label'>
              {t('attendance_settings_checkin')}
            </span>
            <span className='attendance-settings__hint'>
              {t('attendance_settings_checkin_hint')}
            </span>
          </div>
          <SegmentedControl
            aria-label={t('attendance_settings_checkin')}
            options={requirementOptions}
            value={toRequirement(draft.checkInRequired)}
            onChange={(v) => patch({ checkInRequired: v === REQUIRED })}
          />
        </div>

        <hr className='attendance-settings__divider' />

        <div className='attendance-settings__row'>
          <div className='attendance-settings__row-text'>
            <span className='attendance-settings__row-label'>
              {t('attendance_settings_geofence')}
            </span>
            <span className='attendance-settings__hint'>
              {t('attendance_settings_geofence_hint')}
            </span>
          </div>
          <SegmentedControl
            aria-label={t('attendance_settings_geofence')}
            options={requirementOptions}
            value={toRequirement(draft.insideGeoFenceRequired)}
            onChange={(v) => patch({ insideGeoFenceRequired: v === REQUIRED })}
          />
        </div>

        <hr className='attendance-settings__divider' />

        <div className='attendance-settings__row'>
          <div className='attendance-settings__row-text'>
            <span className='attendance-settings__row-label'>{t('attendance_settings_photo')}</span>
            <span className='attendance-settings__hint'>{t('attendance_settings_photo_hint')}</span>
          </div>
          <Switch
            checked={draft.attendanceMediaRequired}
            onChange={(e) => patch({ attendanceMediaRequired: e.target.checked })}
            label={t(
              draft.attendanceMediaRequired ? 'attendance_settings_on' : 'attendance_settings_off',
            )}
            size='lg'
          />
        </div>

        <hr className='attendance-settings__divider' />

        <div className='attendance-settings__row'>
          <div className='attendance-settings__row-text'>
            <span className='attendance-settings__row-label'>
              {t('attendance_settings_api_keys')}
            </span>
            <span className='attendance-settings__hint'>
              {t('attendance_settings_api_keys_hint')}
            </span>
          </div>
          <Button variant='secondary' iconStart='key-round' onClick={() => setApiKeysOpen(true)}>
            {t('attendance_api_keys_button')}
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={minuteConfirmOpen}
        onClose={() => setMinuteConfirmOpen(false)}
        onConfirm={() => {
          patch({ trackLocationPeriod: 1 });
          setMinuteConfirmOpen(false);
        }}
        tone='warning'
        icon='battery-warning'
        title={t('attendance_interval_minute_title')}
        description={t('attendance_interval_minute_body')}
        confirmLabel={t('attendance_interval_minute_confirm')}
      />

      <ApiKeysModal open={apiKeysOpen} onClose={() => setApiKeysOpen(false)} />
    </Drawer>
  );
};

export default AttendanceSettingsDrawer;
